import { type RefObject, useCallback, useEffect, useState } from 'react';

const EDGE_TOLERANCE = 2;
const SCROLL_TOLERANCE = 1;

interface UseNavScrollStateProps {
  navItemsRef: RefObject<HTMLUListElement | null>;
}

interface ScrollState {
  showLeftArrow: boolean;
  showRightArrow: boolean;
}

export const getMaxScrollLeft = (navItems: HTMLUListElement) =>
  Math.max(navItems.scrollWidth - navItems.clientWidth, 0);

const computeScrollState = (navItems: HTMLUListElement): ScrollState => {
  const hasScrollableOverflow = navItems.scrollWidth > navItems.clientWidth + EDGE_TOLERANCE;

  const maxScroll = getMaxScrollLeft(navItems);
  const atEnd = Math.round(navItems.scrollLeft) >= Math.round(maxScroll) - EDGE_TOLERANCE;
  const atStart = navItems.scrollLeft <= SCROLL_TOLERANCE;

  return {
    showLeftArrow: hasScrollableOverflow && !atStart,
    showRightArrow: hasScrollableOverflow && !atEnd,
  };
};

export const useNavScrollState = ({ navItemsRef }: UseNavScrollStateProps) => {
  const [scrollState, setScrollState] = useState<ScrollState>({
    showLeftArrow: false,
    showRightArrow: false,
  });

  const updateScrollState = useCallback(() => {
    const navItems = navItemsRef.current;

    if (!navItems) return;
    setScrollState(computeScrollState(navItems));
  }, [navItemsRef]);

  useEffect(() => {
    const navItems = navItemsRef.current;
    if (!navItems) return;

    updateScrollState();

    navItems.addEventListener('scroll', updateScrollState);

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateScrollState);
      resizeObserver.observe(navItems);
    } else {
      window.addEventListener('resize', updateScrollState);
    }

    return () => {
      navItems.removeEventListener('scroll', updateScrollState);
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', updateScrollState);
      }
    };
  }, [navItemsRef, updateScrollState]);

  return scrollState;
};
